import { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import ReplenishmentReport from '../components/ReplenishmentReport';
import AllSitesLink from '../components/AllSitesLink';
import StaffSignIn from '../components/StaffSignIn';
import useStaffSession from '../hooks/useStaffSession';

// ── Purchasing tool — purchase.silverservice.com ──
//
// Standalone shell for the inventory replenishment / purchasing workflow,
// served from the same SPA bundle as orders./manage. (routed by hostname in
// App.jsx, also reachable via the #purchase hash for staging where the host
// has no `purchase.` prefix).
//
// Behind the shared staff sign-in (hooks/useStaffSession.js), the same one
// Silver Service staff use for manage.silverservice.com.
// All endpoints it calls are already /admin/* gated server-side, so access
// control is enforced by the backend; this shell just gates the UI.
//
// Roles (Operator / Owner) are surfaced from the owner allowlist below so the
// engine fast-follow can hang owner-only controls (cash guardrails, approvals)
// off `isOwner` without reworking this shell. The plumbing phase is read-only
// against SOS — no draft-PO writes happen here yet.
//
// Owner allowlist mirrors backend OWNER_EMAILS (lambda/index.js) and the copy
// in Admin.jsx — keep the three in sync. Backend stays the source of truth for
// access (returns 403); this is UI-only.
const OWNER_EMAILS = new Set([
  'alet.visagie@gmail.com',
  'alet@silverservice.com',
  'olaf@silverservice.com',
]);
function isOwnerEmail(email) {
  return !!email && OWNER_EMAILS.has(String(email).toLowerCase().trim());
}

export default function Purchase() {
  const session = useStaffSession('purchase');
  const { token } = session;
  const [me, setMe] = useState(null);

  // Confirm the sign-in still works via /admin/me; a 401 goes back to sign in.
  useEffect(() => {
    if (token) adminApi.me().then(setMe).catch(() => {});
  }, [token]);

  if (!token) return <StaffSignIn session={session} siteName="Purchasing" />;

  const owner = isOwnerEmail(me?.email);
  const initials = (me?.name || me?.email || 'A')
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('');

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.topBar}>
          <div>
            <h1 style={s.pageTitle}>Silver Service Purchasing</h1>
            <p style={s.sub}>Inventory replenishment & purchase planning</p>
          </div>
          <div style={s.topBarRight}>
            {me && (
              <div style={s.userChip} title={me.email || ''}>
                <div style={s.avatar}>{initials}</div>
                <div style={s.userChipText}>
                  <div style={s.userChipName}>{me.name || me.email}</div>
                  <div style={s.userChipRole}>{owner ? 'Owner' : 'Operator'}</div>
                </div>
              </div>
            )}
            <AllSitesLink style={s.btnOutline} />
            <button style={s.btnOutline} onClick={session.signOut} title="Signs this browser out of every staff site">Sign Out</button>
          </div>
        </div>

        <ReplenishmentReport />
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f5f5f5', display: 'flex', justifyContent: 'center', padding: '2rem 1rem', fontFamily: "'Inter', sans-serif" },
  // Wider than the admin portal's 1100 — the replenishment table has 12
  // columns and needs the room on big screens.
  container: { width: '100%', maxWidth: 1600 },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  pageTitle: { fontSize: '1.5rem', fontWeight: 700, color: '#333', margin: '0 0 4px' },
  sub: { color: '#888', fontSize: '0.85rem', margin: 0 },
  btnOutline: { background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: 8, padding: '0.5rem 1rem', fontSize: '0.82rem', cursor: 'pointer' },
  topBarRight: { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  userChip: { display: 'flex', gap: '0.6rem', alignItems: 'center', padding: '0.35rem 0.75rem', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 24 },
  avatar: { width: 32, height: 32, borderRadius: '50%', background: '#A50B18', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 },
  userChipText: { display: 'flex', flexDirection: 'column' },
  userChipName: { fontSize: '0.85rem', fontWeight: 600, color: '#333', lineHeight: 1.2 },
  userChipRole: { fontSize: '0.7rem', color: 'rgba(0,0,0,0.45)', letterSpacing: '0.05em', textTransform: 'uppercase' },
};
